import styled from 'styled-components'
import { SvgCross } from '../SvgImports'
import { useCallback, useEffect, useRef, useState } from 'react'
import { GuidedViewPageType, SidePanelContent } from '../../store/types'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { BreakpointWidthPx } from '../../css/BreakpointWidthPx'

import {
  closeGvPanel,
  getSidePanelState,
  setGvSidePanelContent,
} from '../../store/sidePanelSlice'
import { ContentSave } from './ContentSave'
import { ContentShare } from './ContentShare'
import { ContentActiveChanges } from './ContentActiveChanges'
import { ContentReadMore } from './ContentReadMore'
import { ContentLogin } from './ContentLogin'
import { ContentGarage } from './ContentGarage'
import { ConsequenceOfChangeHandler } from '../../types/ConsequenceOfChangeTypes'
import { NewTruckLoadedHandler } from '../../types/TruckLoadedTypes'
import { UserConfigInfo } from '../../api/generated'
import { CustomScrollbarMixin } from '../../css/CustomScrollbarMixin'
import { ContentInfo } from './ContentInfo'
import { TestElementTypeId } from '../../types/TestAttributeId'

interface ReadMoreRootProps {
  $sidePanelIsOpen: boolean
}

const SidePanelRoot = styled.div<ReadMoreRootProps>`
  ${CustomScrollbarMixin}
  height: calc(100% - var(--header-height));
  width: 33%;
  background-color: var(--tds-white);
  position: absolute;
  top: var(--header-height);
  overflow-y: scroll;
  border-left: 1px solid var(--tds-grey-300);
  z-index: 100;
  user-select: none;

  --sidepanel-transition-duration: 250ms;
  right: ${(props) => (props.$sidePanelIsOpen ? '0px' : 'calc(0px - 33%)')};
  visibility: ${(props) => (props.$sidePanelIsOpen ? 'visible' : 'hidden')};
  transition-duration: var(--sidepanel-transition-duration);
  transition-property: right, visibility;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px) {
    height: calc(100% - var(--header-height));
    width: 100%;
    top: var(--header-height);
    max-width: 100vw;
    right: ${(props) => (props.$sidePanelIsOpen ? '0px' : 'calc(0px - 100%)')};
  }
`

const CloseIconWrapper = styled.div`
  cursor: pointer;

  svg {
    height: 32px;
    width: 32px;
    margin: 12px;
  }
`

const ContentContainer = styled.div`
  margin: 16px;
`

interface SidePanelProps {
  handleFatalError: () => void
  handleSessionFailure: () => void
  handleConsequenceOfChange: ConsequenceOfChangeHandler
  handleNewTruckIsLoaded: NewTruckLoadedHandler
  handleTruckHasBeenDeleted: (truck: UserConfigInfo) => void
  handleCloseModalConsequenseOfChange: () => void
  handleRfqOrEysClick: () => void
  navigateToMenu: (
    id: string,
    menuPath: string | null | undefined,
    gvPageToJumpTo: GuidedViewPageType,
  ) => void
  menuPath?: string
}

export function SidePanel({
  handleFatalError,
  handleSessionFailure,
  handleCloseModalConsequenseOfChange,
  handleConsequenceOfChange,
  handleNewTruckIsLoaded,
  handleTruckHasBeenDeleted,
  handleRfqOrEysClick,
  navigateToMenu,
  menuPath,
}: SidePanelProps): JSX.Element {
  const dispatch = useAppDispatch()
  const refSidePanel = useRef<HTMLDivElement | null>(null)
  const { gvSidePanelContent, gvSidePanelOpen } =
    useAppSelector(getSidePanelState)
  const [slideInSidePanel, setSlideInSidePanel] = useState<boolean>(false)
  const [aboutVisible, setAboutVisible] = useState<boolean>(false)
  const emptyClickCount = useRef<number>(0)
  const latestEmptyClick = useRef<Date | null>(null)

  useEffect(() => {
    setTimeout(() => {
      setSlideInSidePanel(true)
    }, 50)
  }, [])

  const handleCloseClick = useCallback(() => {
    setSlideInSidePanel(false)
    setTimeout(() => {
      dispatch(closeGvPanel())
      dispatch(setGvSidePanelContent(null))
    }, 250)
  }, [dispatch])

  const handleUserClickOnScreen = useCallback(
    (e: MouseEvent) => {
      if (!gvSidePanelOpen) {
        return
      }
      const currentElement = refSidePanel.current
      const composedPath = e.composedPath()
      const clickIsOutsideSidePanel =
        -1 === composedPath.findIndex((o) => o === currentElement)
      if (clickIsOutsideSidePanel) {
        handleCloseClick()
      }
    },
    [refSidePanel, handleCloseClick, gvSidePanelOpen],
  )

  // Close sidePanel on click outside sidepanel
  useEffect(() => {
    document.addEventListener('click', handleUserClickOnScreen)
    return () => {
      document.removeEventListener('click', handleUserClickOnScreen)
    }
  }, [handleUserClickOnScreen])

  const handleEmptyAreaClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target !== refSidePanel.current) {
      return
    }
    emptyClickCount.current += 1
    latestEmptyClick.current = new Date()
    if (emptyClickCount.current >= 5) {
      setAboutVisible(true)
    }

    setTimeout(() => {
      if (!latestEmptyClick.current) {
        return
      }

      const now = new Date()
      const ms = now.getTime() - latestEmptyClick.current.getTime()
      const allowedTimeBetweenClicksMs = 500
      if (ms < allowedTimeBetweenClicksMs) {
        // Fast enough.
        return
      }

      // Too slow, reset.
      latestEmptyClick.current = null
      emptyClickCount.current = 0
    }, 3000)
  }

  return (
    <SidePanelRoot
      ref={refSidePanel}
      $sidePanelIsOpen={slideInSidePanel}
      onClick={handleEmptyAreaClick}
      data-test-element-type={TestElementTypeId.SidePanel}
    >
      <CloseIconWrapper
        onClick={handleCloseClick}
        data-test-element-type={TestElementTypeId.SidePanelClose}
      >
        <SvgCross />
      </CloseIconWrapper>
      <ContentContainer>
        {gvSidePanelContent === SidePanelContent.save && (
          <ContentSave
            handleFatalError={handleFatalError}
            handleSessionFailure={handleSessionFailure}
          />
        )}
        {gvSidePanelContent === SidePanelContent.share && (
          <ContentShare
            handleFatalError={handleFatalError}
            handleSessionFailure={handleSessionFailure}
            handleRfqOrEysClick={handleRfqOrEysClick}
          />
        )}
        {gvSidePanelContent === SidePanelContent.activeChanges && (
          <ContentActiveChanges
            navigateToMenu={navigateToMenu}
            menuPath={menuPath}
          />
        )}
        {gvSidePanelContent === SidePanelContent.readMore && (
          <ContentReadMore />
        )}
        {gvSidePanelContent === SidePanelContent.info && (
          <ContentInfo aboutVisible={aboutVisible} />
        )}
        {gvSidePanelContent === SidePanelContent.login && (
          <ContentLogin
            handleFatalError={handleFatalError}
            handleSessionFailure={handleSessionFailure}
          />
        )}
        {gvSidePanelContent === SidePanelContent.garage && (
          <ContentGarage
            handleFatalError={handleFatalError}
            handleNewTruckIsLoaded={handleNewTruckIsLoaded}
            handleSessionFailure={handleSessionFailure}
            handleTruckHasBeenDeleted={handleTruckHasBeenDeleted}
            handleConsequenceOfChange={handleConsequenceOfChange}
            handleCloseModalConsequenseOfChange={
              handleCloseModalConsequenseOfChange
            }
          />
        )}
      </ContentContainer>
    </SidePanelRoot>
  )
}
